import React, { useContext, useEffect } from "react";

import classnames from "classnames";
import PropTypes from "prop-types";

import { CURRENCIES } from "components/i18n/source";
import { PropertyListingContext } from "components/store/source";
import { Modal, Text, useMedia } from "components/ui/source";

import BookButton from "./BookButton";

import styles from "./QuoteModal.module.scss";

function QuoteModal({
  className,
  toggleClassName,
  collectionName,
  lineItems,
  loading,
  useMergedDiscount,
  showQuote,
  onClick,
  onClose,
}) {
  const isMobile = useMedia(["$mobile-lg"], [true], false);

  // Show disclaimer only if quote contains base currency item
  const {
    listing: { currency },
  } = useContext(PropertyListingContext);
  const currencySymbol =
    CURRENCIES.find(info => info.code === currency)?.sign || "";
  const isCurrencyEnabled = lineItems?.some(lineItem =>
    ["due_today_in_base_currency", "grand_total_in_base_currency"].includes(
      lineItem.key,
    ),
  );
  // To insert line after base currency item
  const borderIndex = lineItems.findIndex(
    lineItem => lineItem.key === "grand_total_in_base_currency",
  );

  // Do not dismiss the modal during processing booking
  const mobileOnClose = () => {
    if (!isMobile || !loading) onClose();
  };

  const contents = () => (
    <>
      {lineItems.map((item, idx) => (
        <LineItem
          key={item.key}
          {...item}
          isMobile={isMobile}
          name={item.key}
          discountItem={useMergedDiscount && item.key === "promo_discount"}
          noBorder={useMergedDiscount}
          showBorder={
            (useMergedDiscount && item.key === "grand_total") ||
            (isCurrencyEnabled && borderIndex >= 0 && idx == borderIndex + 1)
          }
        />
      ))}

      {isCurrencyEnabled && (
        <Text
          className={styles.currencyDescription}
          types={["caption2"]}
          translateKey="listing:availability.collection.quote.currencyDescription"
          interpolation={{ currency, currencySymbol }}
        />
      )}
    </>
  );

  // Close quote when click any part of the page
  useEffect(() => {
    if (!isMobile) {
      if (showQuote) {
        document?.addEventListener("click", onClose, true);
      } else {
        document?.removeEventListener("click", onClose, true);
      }
    }
    return () => {
      document?.removeEventListener("click", onClose, true);
    };
  }, [onClose, isMobile, showQuote]);

  // Really hate to use useMedia, but we need to show modal on mobile,
  // normal div on desktop mode :(
  if (isMobile)
    return (
      <Modal
        modalProps={{
          isOpen: showQuote,
          onRequestClose: mobileOnClose,
          shouldCloseOnOverlayClick: true,
          className: styles.modal,
        }}
      >
        <Text className={styles.modalTitle} types={["headline4"]}>
          {collectionName}
        </Text>
        {contents()}

        <BookButton
          className={styles.bookButton}
          disabled={loading}
          loading={loading}
          onClick={onClick}
        />
      </Modal>
    );
  else
    return (
      // eslint-disable-next-line
      <div
        className={classnames(
          styles.container,
          className,
          showQuote && toggleClassName,
        )}
        role="dialog"
      >
        {contents()}
      </div>
    );
}

QuoteModal.propTypes = {
  className: PropTypes.string,
  toggleClassName: PropTypes.string,
  collectionName: PropTypes.string,
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.string,
      primary: PropTypes.bool,
    }),
  ),
  loading: PropTypes.bool,
  useMergedDiscount: PropTypes.bool,
  showQuote: PropTypes.bool,
  onClick: PropTypes.func,
  onClose: PropTypes.func,
};

QuoteModal.defaultProps = {
  lineItems: [],
  loading: false,
  showQuote: false,
  onClick: () => {},
  onClose: () => {},
};

function LineItem({
  isMobile,
  label,
  value,
  primary,
  discountItem,
  noBorder,
  showBorder,
}) {
  const textTypes = [
    isMobile ? "caption1" : "caption2",
    primary ? "medium" : "",
    discountItem ? "green" : "",
  ];

  const border = showBorder || (!!primary && !noBorder);

  return (
    <div
      className={classnames(
        styles.lineItemContainer,
        border && styles.borderTop,
      )}
    >
      <Text className={styles.LineItemLabel} types={textTypes}>
        {label}
      </Text>
      <Text className={styles.LineItemValue} types={textTypes}>
        {value}
      </Text>
    </div>
  );
}

LineItem.propTypes = {
  isMobile: PropTypes.bool,
  label: PropTypes.string,
  value: PropTypes.string,
  primary: PropTypes.bool,
  discountItem: PropTypes.bool,
  noBorder: PropTypes.bool,
  showBorder: PropTypes.bool,
};

export default QuoteModal;
